import {
  SimpleFilterAuditDataType,
  SimpleFilterConfiguration,
  SimpleFilterType,
} from '../interfaces'
import { inject, Injectable } from '@angular/core'
import { L10nService } from '@wm-front/admin/l10n'
import {
  AuditReportLineRepairStatus,
  AuditReportType,
  DefectType,
  InputQuoteStatus,
  Qualification,
} from '@wm-front/admin/graphql'
import { translateRepairStatus } from '@wm-front/shared/utils'

@Injectable({ providedIn: 'root' })
export class AuditFiltersCatalogService {
  private _territoryService = inject(L10nService)

  public getConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<any>
  ): SimpleFilterConfiguration<any> {
    if (overrideConfiguration?.dataType !== undefined) {
      switch (overrideConfiguration.dataType) {
        case SimpleFilterAuditDataType.REPAIR_STATUSES:
          return this.getRepairStatusesFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterAuditDataType.AUDIT_REPORT_TYPE:
          return this.getAuditReportTypeFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterAuditDataType.QUOTE_STATUS:
          return this.getQuoteStatusFilterConfiguration(overrideConfiguration)
        case SimpleFilterAuditDataType.DEFECT_TYPE:
          return this.getAuditReportDefectTypeFilterConfiguration(
            overrideConfiguration
          )
        case SimpleFilterAuditDataType.QUALIFICATIONS:
          return this.getAuditReportQualificationFilterConfiguration(
            overrideConfiguration
          )
      }
    }
    return overrideConfiguration
  }

  private getRepairStatusesFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'repairStatuses',
      display: overrideConfiguration.display || {
        label: $localize`:Label | label@@FiltersRepairStatusesButton:Ins. Item status`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      data: Object.keys(AuditReportLineRepairStatus).map((status) => {
        return {
          label: translateRepairStatus(AuditReportLineRepairStatus[status]),
          value: AuditReportLineRepairStatus[status],
        }
      }),
    }
  }

  private getQuoteStatusFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: overrideConfiguration.key || 'quoteStatus',
      display: overrideConfiguration.display || {
        label: $localize`:Label | label@@FiltersQuoteStatusButton:Devis`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      data: [
        {
          label: $localize`:Label | Label for ToSend on quote filter@@filtersStateQuoteToSendTabTitle:Devis à envoyer`,
          value: InputQuoteStatus.ToSend,
        },
        {
          label: $localize`:Label | Label for Sent on quote filter@@filtersStateQuoteSentTabTitle:Devis envoyé`,
          value: InputQuoteStatus.Sent,
        },
        {
          label: $localize`:Label | Label for Rejected on quote filter@@filtersStateQuoteRejectedTabTitle:Devis rejeté`,
          value: InputQuoteStatus.Rejected,
        },
        {
          label: $localize`:Label | Label for to Validated on quote filter@@filtersStateQuoteValidatedTabTitle:Devis validé`,
          value: InputQuoteStatus.Validated,
        },
        {
          label: $localize`:Label | Label for to no quote status on quote filter@@filtersStateNoQuoteTabTitle:Pas de devis`,
          value: InputQuoteStatus.NoQuote,
        },
      ],
    }
  }

  private getAuditReportTypeFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: overrideConfiguration.key || 'auditReportTypes',
      display: overrideConfiguration.display || {
        label: $localize`:Label | label@@FiltersAuditReportTypeButton:Report`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      data: [
        {
          label: $localize`:Label | Label for Périodique on audit report type filter@@filtersStateAuditReportTypePerTitle:Périodique`,
          value: AuditReportType.Periodique,
          territories: ['FR'],
        },
        {
          label: $localize`:Label | Label for Quinquennal on audit report type filter@@filtersStateAuditReportTypeQuinTitle:Quinquennal`,
          value: AuditReportType.Quinquennal,
          territories: ['FR'],
        },
        {
          label: $localize`:Label | Label for Audit on audit report type filter@@filtersStateAuditReportTypeAuditTitle:Audit`,
          value: AuditReportType.Audit,
          territories: ['FR', 'GB'],
        },
        {
          label: $localize`:Label | Label for Audit on audit report type filter@@filtersStateAuditReportTypeInsuranceTitle:Assurance`,
          value: AuditReportType.Insurance,
          territories: ['GB'],
        },
      ].filter((option) =>
        option.territories.includes(
          this._territoryService.territory$() as string
        )
      ),
    }
  }

  private getAuditReportDefectTypeFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'defectTypes',
      display: overrideConfiguration.display || {
        label: $localize`:Label | label@@FiltersDefectTypeButton:Type`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      data: [
        {
          label: $localize`:Label | Label for Immediate on defect type filter@@filtersStateImmediateDefectTypeLabel:Immediate`,
          value: DefectType.Immediate,
        },
        {
          label: $localize`:Label | Label for Immediate on defect type filter@@filtersStateRecommandationDefectTypeLabel:Recommandation`,
          value: DefectType.Recommandation,
        },
        {
          label: $localize`:Label | Label for Immediate on defect type filter@@filtersStateStandardDefectTypeLabel:Standard`,
          value: DefectType.Standard,
        },
        {
          label: $localize`:Label | Label for Immediate on defect type filter@@filtersStateTimedDefectTypeLabel:Timed`,
          value: DefectType.Timed,
        },
      ],
    }
  }

  private getAuditReportQualificationFilterConfiguration(
    overrideConfiguration: SimpleFilterConfiguration<void>
  ): SimpleFilterConfiguration<void> {
    return {
      key: 'qualifications',
      display: overrideConfiguration.display || {
        label: $localize`:Label | label@@FiltersQualificationsButton:Qualifications`,
      },
      type: SimpleFilterType.DROPDOWN,
      withoutSearch: true,
      data: [
        {
          label: $localize`:Label | Label@@auditFiltersCatalogQualificationIncludedInContract:Included in contract`,
          value: Qualification.IncludedInContract,
        },
        {
          label: $localize`:Label | Label@@auditFiltersCatalogQualificationNotIncludedInContract:Not included in contract`,
          value: Qualification.NotIncludedInContract,
        },
      ],
    }
  }
}
